import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus, MasterGroup, Trial } from "@/api/dtos";
import Multiselect, { IMultiselectProps } from "@/components/forms/inputs/Multiselect";
import Text, { ITextProps } from "@/components/forms/inputs/Text";
import TrialContext from "@/contexts/data/TrialContext";
import { faExclamationCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, ListItemIcon, ListItemText } from "@mui/material";
import { FC, useCallback, useContext, useMemo } from "react";
import StatusList, { IStatusListCreateAdditional, IStatusListCreateConfigureLink, IStatusListCreateLabel, IStatusListCreateLink, IStatusListIsDisabled, IStatusListSearch, IStatusListStatsComponentProps, StatusListItemIcon, StatusListItemText, StatusListProps } from "./StatusList";
import { DefaultSortableFields, IStatusListSortable } from "./StatusListSort";
import DefaultStatComponents, { StatusListStatsBox } from './StatusListStats';
import ChosenThemeSizeContext from "@/contexts/styles/ChosenThemeSizeContext";


export interface IMasterGroupConfigurationListAdditional {
    trial?: Trial;
}

export interface IMasterGroupStatusListProps extends StatusListProps<MasterGroup, IMasterGroupConfigurationListAdditional> {

}

const createMasterGroupStatusLabel: IStatusListCreateLabel<MasterGroup, IMasterGroupConfigurationListAdditional> = (itemData) => {
    return (
        <>
            <StatusListItemIcon>
                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.masterGroup} />
            </StatusListItemIcon>
            <StatusListItemText 
                primary={itemData.data.name} 
            />
        </>
    )
};

const createMasterGroupLink: IStatusListCreateLink<MasterGroup, IMasterGroupConfigurationListAdditional> = (itemData) => {
    if (itemData.additional.trial) {
        return `/${itemData.additional.trial.oid}/configuration/master-groups/${itemData.data.code}`
    }

    return `/configuration/master-groups/${itemData.data.code}`;
};

const createMasterGroupConfigurationLink: IStatusListCreateConfigureLink<MasterGroup, IMasterGroupConfigurationListAdditional> = (itemData) => {
    return undefined;
};

const isMasterGroupDisabled: IStatusListIsDisabled<MasterGroup, IMasterGroupConfigurationListAdditional> = (itemData) => {
    return !itemData.data.status || itemData.data.status === ConfigurationStatus.Disabled;
};

const isMasterGroupListItemDisabled: IStatusListIsDisabled<MasterGroup, IMasterGroupConfigurationListAdditional> = (itemData) => {
    return false;
};

const MasterGroupStatsComponent = ({
    itemData, 
    theme
}: IStatusListStatsComponentProps<MasterGroup, IMasterGroupConfigurationListAdditional>) => {
    return (
        
        <StatusListStatsBox>
            <DefaultStatComponents.DisabledIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReadOnlyIndicatorStats itemData={itemData} theme={theme} />
        </StatusListStatsBox>
    )
};


const SortableFields: IStatusListSortable<MasterGroup, IMasterGroupConfigurationListAdditional>[] = [
    {
        name: 'Name',
        sort: (a, b) => a.data.name! > b.data.name! ? 1 : -1
    },
    DefaultSortableFields.status
]

const DefaultSearchData = { statuses: [ConfigurationStatus.Enabled, ConfigurationStatus.ReadOnly] };

const MasterGroupConfigurationList: FC<IMasterGroupStatusListProps> = ({
    ...statusListProps
}) => {
    const { data: trial } = useContext(TrialContext);

    const createMasterGroupAdditional: IStatusListCreateAdditional<MasterGroup, IMasterGroupConfigurationListAdditional> = useCallback((itemData) => {
        return {
            trial: trial,
        }
    }, [trial]);

    

    const searchableFields: IStatusListSearch<MasterGroup, IMasterGroupConfigurationListAdditional>[] = useMemo(() => {
        const output: IStatusListSearch<MasterGroup, IMasterGroupConfigurationListAdditional, any, any>[] = [
            {
                key: 'name',
                component: Text,
                componentProps: {
                    margin: 'normal'
                } as ITextProps,
                label: 'Name',
                searchFn: (item, value) => value ? item.data.name?.toLowerCase()?.includes(value?.toLowerCase()) ?? false : false
            } as IStatusListSearch<MasterGroup, IMasterGroupConfigurationListAdditional, string>
        ]

        output.push({
            key: 'statuses',
            component: Multiselect,
            componentProps: {
                options: [
                    {
                        id: ConfigurationStatus.Disabled,
                        name: 'Disabled',
                        description: `The master group is disabled in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.ReadOnly,
                        name: 'Read Only',
                        description: `The master group is read only in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.Enabled,
                        name: 'Enabled',
                        description: `The master group is enabled in the Source Data Verification system.`
                    }
                ],
                FormControlProps: {
                    margin: 'normal'
                }
                
            } as IMultiselectProps,
            label: 'Statuses',
            searchFn: (item, value) => value && value.length > 0 ?
                item.data.status ?
                    value.includes(item.data.status) ?? false : 
                    false :
                true
        } as IStatusListSearch<MasterGroup, IMasterGroupConfigurationListAdditional, ConfigurationStatus[]>)

        return output;
    }, []);
    

    const themeSize = useContext(ChosenThemeSizeContext);

    const height = useMemo(() => {
        switch (themeSize) {
            case 'extra-small':
                return 25;
            case 'small':
                return 37;
            case 'medium':
                return 49;
            case 'large':
                return 61;
            case 'extra-large':
                return 73;
        }
    }, [themeSize])

    return (
        <StatusList 
            {...statusListProps}
            createLabel={createMasterGroupStatusLabel}
            createLink={createMasterGroupLink}
            createConfigureLink={createMasterGroupConfigurationLink}
            isDisabled={isMasterGroupDisabled}
            isListItemDisabled={isMasterGroupListItemDisabled}
            createAdditional={createMasterGroupAdditional}
            StatsComponent={MasterGroupStatsComponent}
            sortKey="configuration-master-groups-sort"
            sortableFields={SortableFields}
            noRecords={
                <Alert
                    severity="warning"
                >
                    <AlertTitle>No Master Groups</AlertTitle>
                    No master groups were found on the {trial?.name} trial.
                </Alert>
            }
            searchFields={searchableFields}
            searchKey={`configuration-master-groups-search-${trial?.oid}`}
            fixedItemHeight={height}
            defaultSearchData={DefaultSearchData}
        />
    )
};

export default MasterGroupConfigurationList;